import { useMemo } from "react";
import { useJourneyFacts } from "@/hooks/useJourneyFacts";
import { useTeam } from "@/hooks/useTeam";
import { buildJourney, type Journey } from "@/domain/journey";
import { statusCopy, type CopyContext, type StatusCopy } from "@/domain/journeyCopy";
import { firstName } from "@/lib/names";
import type { TeamMember } from "@/data/types";

function productName(journey: Journey | null): string {
  const name = journey?.productName?.trim();
  return name || "policy";
}

/** Names the copy uses; lower-case fallbacks read naturally mid-sentence. */
export function copyContext(team: TeamMember[] | undefined, journey: Journey | null): CopyContext {
  const strategist = team?.find((m) => m.role === "strategist");
  const coordinator = team?.find((m) => m.role === "coordinator");
  return {
    strategist: strategist ? firstName(strategist.name) : "your strategist",
    coordinator: coordinator ? firstName(coordinator.name) : "our team",
    carrier: journey?.carrierName?.trim() || "the carrier",
    product: productName(journey),
  };
}

/** Capitalizes the first letter of each sentence (for lower-case fallbacks). */
export function sentenceCase(text: string): string {
  return text.replace(/(^|[.!?]\s+)([a-z])/g, (_m, lead: string, ch: string) => lead + ch.toUpperCase());
}

function tidy(copy: StatusCopy): StatusCopy {
  return {
    eyebrow: copy.eyebrow,
    title: sentenceCase(copy.title),
    meaning: sentenceCase(copy.meaning),
    next: sentenceCase(copy.next),
    cta: copy.cta ? { ...copy.cta, label: sentenceCase(copy.cta.label) } : null,
  };
}

/** Where the client is, what it means and who is on it. */
export function useJourney() {
  const facts = useJourneyFacts();
  const team = useTeam();
  const journey = useMemo(() => (facts.data ? buildJourney(facts.data) : null), [facts.data]);
  const ctx = useMemo(() => copyContext(team.data, journey), [team.data, journey]);
  const copy = useMemo(() => (journey ? tidy(statusCopy(journey, ctx)) : null), [journey, ctx]);
  return { facts, team, journey, ctx, copy };
}
